<template>
  <div id="app-notifications">
    <app-notification
      position="bottom right"
      class="mb-2"
      group="bottom-notifications"
      :max="5"></app-notification>
    <app-notification
      position="top right"
      class="mt-2 mr-2"
      group="top-notifications"
      :max="5"></app-notification>
  </div>

  <event-triggered-spot-select-dialog v-if="requiresAuth" />
  <event-triggered-asset-select-dialog v-if="requiresAuth" />
  <event-triggered-edit-task-dialog v-if="requiresAuth" />
  <event-triggered-report-issue-dialog v-if="requiresAuth" />
  <event-triggered-clear-issue-dialog v-if="requiresAuth" />

  <RouterView v-slot="{ Component }">
    <VueQueryDevtools v-if="isDev" position="bottom-right" />

    <AuthenticatedPageTemplate v-if="requiresAuth">
      <component :is="Component" />
    </AuthenticatedPageTemplate>
    <component v-else :is="Component" />
  </RouterView>
</template>

<script setup lang="ts">
//TODO: Missing some translations - Go through the components in Circe to ensure they're translated
import { RouterView, useRoute } from 'vue-router';
import { computed } from 'vue';

import AuthenticatedPageTemplate from '@/templates/AuthenticatedPageTemplate.vue';
import AppNotification from '@/components/AppNotification.vue';
import EventTriggeredAssetSelectDialog from '@/components/EventTriggeredAssetSelectDialog.vue';
import EventTriggeredClearIssueDialog from '@/components/EventTriggeredClearIssueDialog.vue';
import EventTriggeredEditTaskDialog from '@/components/EventTriggeredEditTaskDialog.vue';
import EventTriggeredReportIssueDialog from '@/components/EventTriggeredReportIssueDialog.vue';
import EventTriggeredSpotSelectDialog from '@/components/EventTriggeredSpotSelectDialog.vue';

const route = useRoute();
const requiresAuth = computed(() => route.matched.some(r => r.meta.requireAuth));
const isDev = import.meta.env.DEV;
</script>

<style lang="scss">
@use '@/styles/main.scss';

.font--poppins {
  font-family: 'Poppins', sans-serif !important;
}

h3 {
  font-size: $font-size-5;
}

h4 {
  font-size: $font-size-4;
}
#app-notifications {
  .vue-notification-group {
    min-width: 250px !important;
    width: auto !important;
    max-width: 500px;
    right: 1rem !important;
    z-index: 10000000000000;
  }

  &.is-mobile {
    .vue-notification-group {
      width: 90% !important;
      left: 5% !important;
    }
  }

  .vue-notification-wrapper {
    &:not(:first-child) {
      margin-top: 0.5rem;
    }
  }

  .app-notification {
    border-radius: 5px;
    position: relative;
    padding: 0.5rem 1rem;

    i.type-icon {
      color: $white;
    }

    &.success {
      background-color: $white !important;
      border: 1px solid getVuetifyColor(success) !important;
      border-left: 40px solid getVuetifyColor(success) !important;
      color: $black;
    }

    &.error {
      background-color: $white !important;
      border: 1px solid getVuetifyColor(error) !important;
      border-left: 40px solid getVuetifyColor(error) !important;
      color: $black;
    }

    &.warning,
    &.warn {
      background-color: $white !important;
      border: 1px solid getVuetifyColor(warning) !important;
      border-left: 40px solid getVuetifyColor(warning) !important;
      color: $black;
    }

    &.info {
      background-color: $white !important;
      border: 1px solid $color-accent-60 !important;
      border-left: 40px solid $color-accent-60 !important;
      color: $black;
    }

    .title {
      font-size: 1rem !important;
      margin-bottom: 0;
      line-height: 1.25rem;
    }

    i.type-icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 0;
      margin-left: -35px;
      font-size: 2rem;
      z-index: 15;
    }

    .close {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      padding: 0 0.5rem;

      i {
        font-size: 12px;
      }
    }
  }
}

@media (max-width: 600px) {
  #app-notifications {
    .vue-notification-group {
      max-width: calc(100% - 2rem);
    }
  }
}
</style>

<style lang="scss" scoped>
header {
  line-height: 1.5;
  max-height: 100dvh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}
</style>
